/**
 * v149 — `ads` block. Shows the Quick Promote ads Linkola serves to this
 * page (always rendered after the page's other blocks) as a vertical feed:
 * one full-width ad below another, scrolled by the visitor like an
 * Instagram feed. There is no carousel and no auto-scroll (v149.2).
 *
 * Each ad reports an impression once it is ≥50% on screen (or fills half
 * the viewport) for 1 s while the tab is visible; views (video plays) and
 * clicks are reported when they happen. Every event goes out once per ad
 * per page view, tagged with the Smart QR the visitor scanned (if any).
 * Billing — one visitor pays only the highest price among the actions they
 * took on each ad — and device de-duplication happen server-side.
 *
 * Preview (v149.2): `?qp_preview={ad_id}` — the advertiser's "View live"
 * link — pins that ad first and highlights it (or explains why it can't
 * show here). Nothing is tracked or charged on a preview page view.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Megaphone } from "lucide-react";
import { api } from "../../lib/api";
import { getVisitQr, trackAdEvent } from "../../lib/quickPromote";
import PromoteAdCard from "../quickPromote/AdCards";

const IMPRESSION_VISIBLE_RATIO = 0.5;
const IMPRESSION_DWELL_MS = 1000;
const PREVIEW_REASONS = {
  outside_target_area: "this Web Link is outside the ad's target area (location + radius).",
  category_excluded: "this shop's category is excluded on the ad.",
  paused: "the ad is paused.",
  budget_used_up: "the ad's budget is used up.",
  suspended: "Linkola suspended the ad.",
  own_page: "ads never show on the advertiser's own page.",
  page_not_eligible: "this page can't show ads right now (needs an active Linkola QR, a business location and the ads block).",
  content_unavailable: "the promoted item is hidden, expired or sold out.",
  quick_promote_disabled: "Quick Promote is paused by Linkola.",
  not_found: "the ad was not found.",
};

function readPreviewAdId() {
  try {
    const id = new URLSearchParams(window.location.search).get("qp_preview") || "";
    return /^qpa_[a-z0-9]{6,32}$/i.test(id) ? id : null;
  } catch {
    return null;
  }
}

export default function AdsBlock({ block, hubContext, editable = false }) {
  const c = block?.config || {};
  const heading = (c.heading || "Sponsored").trim() || "Sponsored";
  const slug = hubContext?.creator?.slug || hubContext?.slug || null;
  const pageId = hubContext?.page_id || null;
  const [ads, setAds] = useState([]);
  const [preview, setPreview] = useState(null);
  const [playingId, setPlayingId] = useState(null);
  const previewAdId = useMemo(() => (editable ? null : readPreviewAdId()), [editable]);
  const trackedRef = useRef(new Set());
  const cardsRef = useRef(new Map());
  const playingRef = useRef(null);
  playingRef.current = playingId;

  useEffect(() => {
    if (editable || !slug) return undefined;
    let alive = true;
    api.get("/public/quick-promote/feed", {
      params: { slug, page_id: pageId || undefined, qr: getVisitQr(slug) || undefined, preview_ad: previewAdId || undefined },
    })
      .then(({ data }) => {
        if (!alive) return;
        setAds(Array.isArray(data?.ads) ? data.ads : []);
        setPreview(data?.preview || null);
      })
      .catch(() => { if (alive) setAds([]); });
    return () => { alive = false; };
  }, [slug, pageId, editable, previewAdId]);

  // "View live": bring the advertiser's ad into view once it has loaded.
  useEffect(() => {
    if (!preview?.shown || !previewAdId) return undefined;
    const t = setTimeout(() => cardsRef.current.get(previewAdId)?.scrollIntoView?.({ block: "center" }), 400);
    return () => clearTimeout(t);
  }, [preview, previewAdId]);

  const record = useCallback((adId, event) => {
    if (previewAdId) return; // previews are never tracked or charged
    const key = `${adId}:${event}`;
    if (trackedRef.current.has(key)) return;
    trackedRef.current.add(key);
    trackAdEvent({ adId, slug, pageId, event });
  }, [slug, pageId, previewAdId]);

  const adIds = ads.map((a) => a.ad_id).join("|");

  // Impressions for every ad in the feed + stop a video once it scrolls away.
  useEffect(() => {
    if (editable || !slug || !adIds || previewAdId) return undefined;
    const state = new Map(); // adId → { inView, timer }
    const stop = (adId) => {
      const st = state.get(adId);
      if (st?.timer) { clearTimeout(st.timer); st.timer = null; }
    };
    const arm = (adId) => {
      const st = state.get(adId);
      if (!st || st.timer || !st.inView || document.hidden || trackedRef.current.has(`${adId}:impression`)) return;
      st.timer = setTimeout(() => {
        st.timer = null;
        if (st.inView && !document.hidden) record(adId, "impression");
      }, IMPRESSION_DWELL_MS);
    };
    const onVisibility = () => state.forEach((_, adId) => (document.hidden ? stop(adId) : arm(adId)));
    document.addEventListener("visibilitychange", onVisibility);

    const ids = adIds.split("|");
    let observer = null;
    if (typeof IntersectionObserver === "undefined") {
      ids.forEach((id) => { state.set(id, { inView: true, timer: null }); arm(id); });
    } else {
      observer = new IntersectionObserver((entries) => {
        const vh = window.innerHeight || document.documentElement.clientHeight || 0;
        entries.forEach((entry) => {
          const adId = entry.target.getAttribute("data-ad-feed-id");
          const st = state.get(adId);
          if (!st) return;
          st.inView = entry.isIntersecting && (entry.intersectionRatio >= IMPRESSION_VISIBLE_RATIO
            || (vh > 0 && entry.intersectionRect.height >= vh * IMPRESSION_VISIBLE_RATIO));
          if (st.inView) arm(adId); else stop(adId);
          if (!entry.isIntersecting && playingRef.current === adId) setPlayingId(null);
        });
      }, { threshold: [0, 0.25, 0.5, 0.75, 1] });
      ids.forEach((id) => {
        const el = cardsRef.current.get(id);
        if (!el) return;
        state.set(id, { inView: false, timer: null });
        observer.observe(el);
      });
    }
    return () => {
      state.forEach((_, adId) => stop(adId));
      observer?.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [adIds, slug, editable, record, previewAdId]);

  if (editable) {
    return (
      <div className="border-2 border-dashed border-ink/40 bg-white text-ink p-4 text-center" data-testid={`block-ads-preview-${block.block_id}`}>
        <div className="mx-auto w-10 h-10 border-2 border-ink bg-sun flex items-center justify-center mb-2">
          <Megaphone className="w-5 h-5" strokeWidth={2.5} />
        </div>
        <p className="font-bold text-sm">{heading} · Ads block</p>
        <p className="text-xs text-ink/60 mt-1">
          Local promotions from Linkola appear here one below another, after all your other blocks.
          You earn when visitors see, watch or tap an ad.
        </p>
      </div>
    );
  }
  const previewMissing = previewAdId && preview && !preview.shown;
  if (!ads.length && !previewMissing) return null;

  return (
    <section className="w-full" aria-label={heading} data-testid={`block-ads-${block.block_id}`}>
      <p className="text-[11px] font-bold uppercase tracking-wider ah-muted inline-flex items-center gap-1.5 mb-2">
        <Megaphone className="w-3.5 h-3.5" /> {heading}
      </p>
      {previewMissing && (
        <div className="mb-4 border-2 border-ink bg-sun text-ink p-3 text-sm" role="status" data-testid="block-ads-preview-missing">
          <b>Your ad isn&apos;t shown on this page:</b> {PREVIEW_REASONS[preview.reason] || preview.reason}
        </div>
      )}
      <ul className="flex flex-col gap-5" data-testid={`block-ads-feed-${block.block_id}`}>
        {ads.map((ad) => (
          <li
            key={ad.ad_id}
            ref={(el) => { if (el) cardsRef.current.set(ad.ad_id, el); else cardsRef.current.delete(ad.ad_id); }}
            data-ad-feed-id={ad.ad_id}
            className={`min-w-0 ${ad.ad_id === previewAdId ? "outline outline-4 outline-offset-4 outline-[#FF3B30]" : ""}`}
          >
            {ad.ad_id === previewAdId && (
              <p className="mb-2 inline-flex items-center border-2 border-ink bg-[#FF3B30] text-white text-[11px] font-black uppercase tracking-wider px-2 py-0.5" data-testid="block-ads-preview-label">
                Your ad · live preview (not charged)
              </p>
            )}
            <PromoteAdCard
              ad={ad}
              playing={playingId === ad.ad_id}
              onPlayingChange={(on) => setPlayingId(on ? ad.ad_id : null)}
              onEngage={(event) => {
                // Tapping an ad means it was seen, even if it left the screen before 1 s.
                record(ad.ad_id, "impression");
                record(ad.ad_id, event);
              }}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}
